import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  TextField,
  Button,
  IconButton,
  Chip,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Snackbar,
  Drawer,
  AppBar,
  Toolbar,
  Badge,
} from "@mui/material";
import {
  Search,
  Clear,
  Add,
  Remove,
  Delete,
  ShoppingCart,
  Person,
  Receipt,
  Note,
  Discount,
  List as ListIcon,
  Payment,
  AccountBalance,
  CreditCard,
  Money,
  Pause,
  ClearAll,
  Print,
  RestaurantMenu,
  Menu as MenuIcon,
  Close as CloseIcon,
  CardGiftcard,
} from "@mui/icons-material";
import {
  productsAPI,
  customersAPI,
  transactionsAPI,
  settingsAPI,
  heldTransactionsAPI,
  apiUtils,
} from "../services/api";
import ThermalReceiptPreview from "../components/ThermalReceiptPreview";
import useResponsive from "../hooks/useResponsive";
import OrderSlipPreview from "../components/OrderSlipPreview";
import { printerService } from "../services/printer";


const POS = () => {
  // Responsive hook for mobile detection
  const { isMobile } = useResponsive();

  // Mobile cart drawer state
  const [mobileCartOpen, setMobileCartOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");

  // Products state
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState(["All Categories"]);

  // Cart state
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerInput, setCustomerInput] = useState("");

  // Computed values
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);

  // Service charge state
  const [serviceChargeEnabled, setServiceChargeEnabled] = useState(false);
  const [serviceChargePercentage, setServiceChargePercentage] = useState(0);
  const [serviceChargeAmount, setServiceChargeAmount] = useState(0);
  const [serviceChargeInclusive, setServiceChargeInclusive] = useState(false);

  // Note and Discount state
  const [transactionNote, setTransactionNote] = useState("");
  const [discounts, setDiscounts] = useState([]);
  const [totalDiscount, setTotalDiscount] = useState(0);

  // Dialog state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [savedTransactionsDialogOpen, setSavedTransactionsDialogOpen] =
    useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  const [tempNote, setTempNote] = useState("");
  const [tempDiscount, setTempDiscount] = useState({
    type: "Store",
    discount_type: "amount", // 'amount' or 'percent'
    discount_amount: 0,
    discount_percent: 0,
    reference1: "",
    reference2: "",
  });

  // Saved transactions state
  const [savedTransactions, setSavedTransactions] = useState([]);

  // Payment state
  const [payments, setPayments] = useState([]);
  const [currentPayment, setCurrentPayment] = useState({
    payment_type: "CASH",
    reference1: "",
    reference2: "",
    payment_amount: 0,
  });

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
    showPrintButton: false,
    transactionData: null,
  });

  // Receipt Preview state
  const [receiptPreviewOpen, setReceiptPreviewOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  // Order Slip state
  const [orderSlipPreviewOpen, setOrderSlipPreviewOpen] = useState(false);
  const [orderSlipData, setOrderSlipData] = useState(null);
  const [orderSlipEnabled, setOrderSlipEnabled] = useState(false);
  
  // Thermal Printer state
  const [thermalWidth, setThermalWidth] = useState(58);
  
  // Printer settings state
  const [printerSettings, setPrinterSettings] = useState(null);

  useEffect(() => {
    loadInitialData();
    loadSavedTransactions();
    loadPOSSettings();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, products, selectedCategory]);

  useEffect(() => {
    calculateTotals();
  }, [cart, discounts, serviceChargeEnabled, serviceChargePercentage, serviceChargeInclusive]);

  // Auto-close mobile cart when cart becomes empty
  useEffect(() => {
    if (cart.length === 0 && mobileCartOpen) {
      setMobileCartOpen(false);
    }
  }, [cart.length, mobileCartOpen]);

  // Helper function to show notifications
  const showNotification = (
    message,
    severity = "success",
    showPrintButton = false,
    transactionData = null
  ) => {
    setNotification({
      open: true,
      message,
      severity,
      showPrintButton,
      transactionData,
    });
  };

  const handleNotificationClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handlePrintReceiptClick = async () => {
    if (notification.transactionData) {
      console.log('handlePrintReceiptClick called');
      console.log('UI printerSettings:', printerSettings);
      console.log('UI printerSettings.printer_type:', printerSettings?.printer_type);
      
      // Check if advanced printing is configured
      if (printerSettings && printerSettings.printer_type !== 'browser') {
        console.log('Advanced printing branch - calling printerService.printReceipt');
        try {
          // Let printer service load its own fresh settings
          // printerService.settings = printerSettings; // REMOVED - let service load fresh settings
          
          // Try to print using advanced printer service
          await printerService.printReceipt(notification.transactionData);
          showNotification('Receipt printed successfully', 'success');
        } catch (error) {
          console.error('Advanced printing failed:', error);
          
          if (error.message === 'BROWSER_PRINT_REQUESTED') {
            // Printer service is requesting browser print - show preview
            console.log('Showing receipt preview as requested by printer service');
            setReceiptData(notification.transactionData);
            setReceiptPreviewOpen(true);
          } else {
            showNotification(`Printing failed: ${error.message}. Opening print preview...`, 'warning');
            
            // Fall back to browser print preview
            setReceiptData(notification.transactionData);
            setReceiptPreviewOpen(true);
          }
        }
      } else {
        // Browser printing is selected - show preview directly
        console.log('Browser printing branch - showing preview directly');
        setReceiptData(notification.transactionData);
        setReceiptPreviewOpen(true);
      }
    }
    handleNotificationClose();
  };

  const handleOrderSlipClick = () => {
    if (cart.length === 0) {
      showNotification('Cart is empty', 'warning');
      return;
    }

    const orderNumber = `ORD-${Date.now()}`;
    const orderData = {
      orderNumber,
      timestamp: new Date().toISOString(),
      customer: customer,
      server: JSON.parse(localStorage.getItem('user') || '{}').username || 'POS',
      items: cart.map(item => ({
        name: item.name,
        qty: item.qty,
        price: item.isComplimentary ? 0 : item.price,
        unit_price: item.isComplimentary ? 0 : item.price,
        isComplimentary: item.isComplimentary,
        note: item.isComplimentary ? 'COMPLIMENTARY' : '' // Flag complimentary items
      })),
      note: transactionNote,
      subtotal,
      tax,
      total,
      totalDiscount,
      serviceChargeAmount,
      serviceChargeInclusive
    };

    setOrderSlipData(orderData);
    setOrderSlipPreviewOpen(true);
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      console.log("Loading initial data...");
      const [productsResponse, customersResponse] = await Promise.all([
        productsAPI.list(),
        customersAPI.list(),
      ]);

      const productsData = apiUtils.formatResponse(productsResponse) || [];
      const customersData = apiUtils.formatResponse(customersResponse) || [];

      console.log("Formatted products data:", productsData);
      console.log("Formatted customers data:", customersData);

      // Filter out inactive products (only show active products in POS)
      const activeProducts = productsData.filter(product => product.active === 1);
      console.log("Active products filtered:", activeProducts.length, "of", productsData.length, "total products");

      setProducts(activeProducts);
      setCustomers(customersData);

      // Extract unique categories from active products only
      const uniqueCategories = [...new Set(activeProducts
        .map(product => product.category)
        .filter(category => category && category.trim() !== "")
      )].sort();
      
      setCategories(["All Categories", ...uniqueCategories]);

      console.log("Active products loaded:", activeProducts.length, "items");
      console.log("Customers loaded:", customersData.length, "items");
      console.log("Categories found:", uniqueCategories);

      // If no active products are loaded, add some sample data for testing
      if (activeProducts.length === 0) {
        console.log("No active products found, adding sample data for testing...");
        const sampleProducts = [];
        setProducts(sampleProducts);
        setFilteredProducts(sampleProducts); // Set filtered products immediately
      } else {
        setFilteredProducts(activeProducts); // Set filtered products immediately
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      setError(apiUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh products with updated stock levels
  const refreshProductStock = async () => {
    try {
      console.log("Refreshing product stock...");
      const productsResponse = await productsAPI.list();
      const productsData = apiUtils.formatResponse(productsResponse) || [];
      
      // Filter out inactive products (only show active products in POS)
      const activeProducts = productsData.filter(product => product.active === 1);
      console.log("Product stock refreshed:", activeProducts.length, "active products");
      
      setProducts(activeProducts);
      
      // Update cart items with new stock levels
      setCart(prevCart => 
        prevCart.map(cartItem => {
          const updatedProduct = activeProducts.find(p => p.id === cartItem.id);
          if (updatedProduct) {
            return {
              ...cartItem,
              on_hand: updatedProduct.on_hand || updatedProduct.stock_quantity || 0
            };
          }
          return cartItem;
        })
      );
      
      // Update filtered products based on current search and category filters
      let filtered = activeProducts;
      
      // Apply category filter
      if (selectedCategory && selectedCategory !== "All Categories") {
        filtered = filtered.filter(product => product.category === selectedCategory);
      }
      
      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(product =>
          product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setFilteredProducts(filtered);
    } catch (error) {
      console.error("Error refreshing product stock:", error);
      // Don't show error to user as this is a background operation
    }
  };

  const loadPOSSettings = async () => {
    try {
      const response = await settingsAPI.fetch();
      const settings = apiUtils.formatResponse(response);
      
      if (settings) {
        console.log("Raw settings from API:", settings);
        setServiceChargeEnabled(Boolean(settings.service_charge_enabled));
        // Parse service charge percentage as a number (it comes as string from DB)
        let scPercentage = parseFloat(settings.service_charge_percentage) || 0;
        console.log("Service charge percentage parsed to number:", scPercentage, "(from raw string:", settings.service_charge_percentage, ")");
        setServiceChargePercentage(scPercentage);
        setServiceChargeInclusive(Boolean(settings.service_charge_inclusive));
        setOrderSlipEnabled(Boolean(settings.order_slip_enabled));
        setThermalWidth(settings.thermal_printer_width || 58);
        
        // Load printer settings from localStorage (device-specific, not from API)
        loadPrinterSettingsFromLocalStorage();
      }
    } catch (error) {
      console.error("Error loading POS settings:", error);
      // Don't show error to user, just continue with defaults
    }
  };

  // Function to load printer settings from localStorage
  const loadPrinterSettingsFromLocalStorage = () => {
    try {
      const printerType = localStorage.getItem('printer-type') || 'browser';
      const qzTrayEnabled = localStorage.getItem('qz-tray-enabled') === 'true';
      const qzTrayPrinterName = localStorage.getItem('qz-tray-printer-name') || null;
      const rawbtEnabled = localStorage.getItem('rawbt-enabled') === 'true';
      const rawbtDeviceName = localStorage.getItem('rawbt-device-name') || null;
      const rawbtDeviceAddress = localStorage.getItem('rawbt-device-address') || null;
      
      console.log("Loading printer settings from localStorage:", {
        printerType, qzTrayEnabled, qzTrayPrinterName, rawbtEnabled, rawbtDeviceName, rawbtDeviceAddress
      });
      
      setPrinterSettings({
        printer_type: printerType,
        qz_tray_enabled: qzTrayEnabled,
        qz_tray_printer_name: qzTrayPrinterName,
        rawbt_enabled: rawbtEnabled,
        rawbt_device_name: rawbtDeviceName,
        rawbt_device_address: rawbtDeviceAddress
      });
    } catch (error) {
      console.error("Error loading printer settings from localStorage:", error);
      // Use defaults
      setPrinterSettings({
        printer_type: 'browser',
        qz_tray_enabled: false,
        qz_tray_printer_name: null,
        rawbt_enabled: false,
        rawbt_device_name: null,
        rawbt_device_address: null
      });
    }
  };

  const filterProducts = () => {
    let filtered = products;

    // Filter by category first
    if (selectedCategory !== "All Categories") {
      filtered = filtered.filter(product => 
        product.category === selectedCategory
      );
    }

    // Then filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (product) =>
          product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  const addToCart = (product) => {
    // Check if product has any stock available
    const availableStock = product.on_hand || product.stock_quantity || 0;
    
    if (availableStock <= 0) {
      showNotification('Product is out of stock', 'warning');
      return;
    }

    // Find existing item with same product ID and complimentary status
    // Note: complimentary items are always separate from regular items
    const existingItem = cart.find((item) => item.id === product.id && item.isComplimentary === false);

    // Determine the correct price property
    const productPrice = product.price || 0;
    console.log("Using price:", productPrice);

    if (existingItem) {
      // Check if adding one more would exceed available stock
      // Consider total quantity of both regular and complimentary items
      const totalQuantity = cart
        .filter(item => item.id === product.id)
        .reduce((sum, item) => sum + item.qty, 0);
      
      if (totalQuantity >= availableStock) {
        showNotification('Cannot add more items. Insufficient stock.', 'warning');
        return;
      }

      const newCart = cart.map((item) =>
        item.id === product.id && item.isComplimentary === false 
          ? { ...item, qty: item.qty + 1 } 
          : item
      );
      console.log("Updated existing item, new cart:", newCart);
      setCart(newCart);
    } else {
      const newItem = {
        id: product.id,
        name: product.name,
        sku: product.code_name || product.sku || "", // Use code_name as fallback
        price: parseFloat(productPrice),
        qty: 1,
        on_hand: availableStock,
        image: product.image_url,
        isComplimentary: false, // Default to not complimentary
        cartItemId: Date.now(), // Unique identifier for this cart entry
      };
      console.log("New item being added:", newItem);
      const newCart = [...cart, newItem];
      console.log("Added new item, new cart:", newCart);
      setCart(newCart);
    }
  };

  // Toggle complimentary status for a cart item
  const toggleComplimentary = (productId, currentComplimentaryStatus) => {
    // When toggling complimentary status, we need to check stock availability
    // Get total stock for this product across all cart entries
    const totalQuantityForProduct = cart
      .filter(item => item.id === productId)
      .reduce((sum, item) => sum + item.qty, 0);
    
    // Get the on_hand from any cart item with this product
    const cartItem = cart.find(item => item.id === productId);
    const availableStock = cartItem?.on_hand || 0;
    
    // Find the current item we're toggling
    const currentItem = cart.find(
      item => item.id === productId && item.isComplimentary === currentComplimentaryStatus
    );
    
    if (!currentItem) return;
    
    // Check if there's already an item with opposite complimentary status
    const oppositeItem = cart.find(
      item => item.id === productId && item.isComplimentary === !currentComplimentaryStatus
    );
    
    if (oppositeItem) {
      // Merge quantities into the opposite item and remove current item
      const newCart = cart
        .map(item => {
          if (item.id === productId && item.isComplimentary === !currentComplimentaryStatus) {
            return { ...item, qty: item.qty + currentItem.qty };
          }
          return item;
        })
        .filter(item => !(item.id === productId && item.isComplimentary === currentComplimentaryStatus));
      
      setCart(newCart);
    } else {
      // Just toggle the flag on the current item
      const newCart = cart.map(item => {
        if (item.id === productId && item.isComplimentary === currentComplimentaryStatus) {
          return { ...item, isComplimentary: !currentComplimentaryStatus };
        }
        return item;
      });
      
      setCart(newCart);
    }
  };

  // Note and Discount handlers
  const handleNoteDialogOpen = () => {
    setTempNote(transactionNote);
    setNoteDialogOpen(true);
  };

  const handleNoteDialogClose = () => {
    setNoteDialogOpen(false);
    setTempNote("");
  };

  const handleNoteSave = () => {
    setTransactionNote(tempNote);
    handleNoteDialogClose();
  };

  const handleDiscountDialogOpen = () => {
    setTempDiscount({
      type: "Store",
      discount_type: "amount",
      discount_amount: 0,
      discount_percent: 0,
      reference1: "",
      reference2: "",
    });
    setDiscountDialogOpen(true);
  };

  const handleDiscountDialogClose = () => {
    setDiscountDialogOpen(false);
  };

  const handleDiscountSave = () => {
    const newDiscount = {
      ...tempDiscount,
      id: Date.now(), // Simple ID generation
    };
    setDiscounts([...discounts, newDiscount]);
    handleDiscountDialogClose();
  };

  const removeDiscount = (discountId) => {
    setDiscounts(discounts.filter((d) => d.id !== discountId));
  };

  const updateCartQuantity = (cartItemId, newQty) => {
    if (newQty <= 0) {
      removeFromCart(cartItemId);
      return;
    }

    // Find the cart item to check stock limits
    const cartItem = cart.find(item => item.cartItemId === cartItemId);
    if (!cartItem) return;
    
    // Check total quantity for this product ID across all cart entries
    const totalQuantityForProduct = cart
      .filter(item => item.id === cartItem.id)
      .reduce((sum, item) => sum + item.qty, 0);
    
    const quantityDifference = newQty - cartItem.qty;
    
    if (totalQuantityForProduct + quantityDifference > cartItem.on_hand) {
      showNotification('Cannot set quantity higher than available stock', 'warning');
      return;
    }

    setCart(
      cart.map((item) =>
        item.cartItemId === cartItemId ? { ...item, qty: newQty } : item
      )
    );
  };

  const removeFromCart = (cartItemId) => {
    setCart(cart.filter((item) => item.cartItemId !== cartItemId));
  };

  const clearCart = () => {
    setCart([]);
    setCustomer(null);
    setCustomerInput("");
  };

  const calculateTotals = () => {
    console.log("Calculating totals for cart:", cart);
    
    // Step 1: Calculate gross total (what customer ordered) - exclude complimentary items
    const grossTotal = cart.reduce((sum, item) => {
      // Complimentary items don't contribute to the gross total
      if (item.isComplimentary) {
        console.log(`Item: ${item.name}, COMPLIMENTARY - Price: 0`);
        return sum;
      }
      console.log(
        `Item: ${item.name}, Price: ${item.price}, Qty: ${
          item.qty
        }, Subtotal: ${item.price * item.qty}`
      );
      return sum + item.price * item.qty;
    }, 0);

    // Step 2: Calculate total discount amount
    const newTotalDiscount = discounts.reduce((sum, discount) => {
      if (discount.discount_type === "amount") {
        return sum + discount.discount_amount;
      } else if (discount.discount_type === "percent") {
        // Discounts apply to net of VAT (VAT-exclusive amount)
        const vatExclusiveAmount = grossTotal / 1.12;
        const discountAmount = (vatExclusiveAmount * discount.discount_percent) / 100;
        // Convert back to VAT-inclusive amount for proper total calculation
        return sum + (discountAmount * 1.12);
      }
      return sum;
    }, 0);

    // Step 3: Calculate service charge based on gross selling price (before discounts)
    let newServiceChargeAmount = 0;
    if (serviceChargeEnabled && serviceChargePercentage > 0) {
      console.log("Service charge calculation - serviceChargePercentage:", serviceChargePercentage);
      console.log("Service charge calculation - grossTotal:", grossTotal);
      // Service charge is always based on gross selling price
      newServiceChargeAmount = (grossTotal * serviceChargePercentage) / 100;
      console.log("Service charge calculation - newServiceChargeAmount:", newServiceChargeAmount);
    }

    // Step 4: Calculate final total based on service charge mode
    let newTotal = 0;
    if (serviceChargeEnabled && serviceChargePercentage > 0) {
      if (serviceChargeInclusive) {
        // Inclusive: Customer pays what they ordered (grossTotal), service charge is included
        newTotal = grossTotal - newTotalDiscount;
        console.log("Inclusive calculation - newTotal before SC recalc:", newTotal);
        console.log("Inclusive calculation - serviceChargePercentage:", serviceChargePercentage);
        // Recalculate service charge amount from the final total using inclusive formula
        newServiceChargeAmount = (newTotal * serviceChargePercentage) / (100 + serviceChargePercentage);
        console.log("Inclusive calculation - recalculated newServiceChargeAmount:", newServiceChargeAmount);
      } else {
        // Exclusive: Add service charge to what customer ordered
        newTotal = grossTotal - newTotalDiscount + newServiceChargeAmount;
      }
    } else {
      // No service charge: Customer pays what they ordered minus discounts
      newTotal = grossTotal - newTotalDiscount;
    }

    // Step 5: Extract VAT (always 12% inclusive)
    const newTax = (newTotal * 12) / 112;
    
    // Step 6: Calculate subtotal (VAT-exclusive amount)
    const newSubtotal = newTotal - newTax;

    console.log(
      `Calculated (${serviceChargeInclusive ? 'Inclusive' : serviceChargeEnabled ? 'Exclusive' : 'No'} SC) - Gross: ${grossTotal}, Discount: ${newTotalDiscount}, Service Charge: ${newServiceChargeAmount}, Total: ${newTotal}, Subtotal: ${newSubtotal}, VAT: ${newTax}`
    );

    setSubtotal(newSubtotal);
    setTotalDiscount(newTotalDiscount);
    setTax(newTax);
    setServiceChargeAmount(newServiceChargeAmount);
    setTotal(newTotal);
  };

  const handleCustomerChange = (event, newValue) => {
    if (typeof newValue === "string") {
      // User typed a new customer name
      setCustomerInput(newValue);
      setCustomer({ display_name: newValue, isNew: true });
    } else if (newValue && newValue.inputValue) {
      // User selected "Add new customer" option
      setCustomerInput(newValue.inputValue);
      setCustomer({ display_name: newValue.inputValue, isNew: true });
    } else {
      // User selected existing customer
      setCustomer(newValue);
      setCustomerInput(newValue?.display_name || "");
    }
  };

  const processTransaction = async () => {
    if (cart.length === 0) {
      setError("Cart is empty");
      return;
    }

    try {
      setLoading(true);

      // First, create customer if it's a new one
      let customerId = customer?.id || 0;
      if (customer?.isNew && customer.display_name) {
        try {
          const newCustomerData = {
            display_name: customer.display_name,
            first_name: customer.display_name.split(" ")[0] || "",
            last_name:
              customer.display_name.split(" ").slice(1).join(" ") || "",
            mobile: "",
            address: "",
            notes: "Added from POS",
          };

          const customerResponse = await customersAPI.create(newCustomerData);
          const createdCustomer = apiUtils.formatResponse(customerResponse);
          customerId = createdCustomer.id;

          // Update customers list
          setCustomers([...customers, createdCustomer]);
        } catch (customerError) {
          console.warn(
            "Failed to create customer, using walk-in:",
            customerError
          );
          customerId = 0;
        }
      }

      // Prepare transaction data
      const transactionData = {
        customer_id: customerId,
        customer_name: customer?.display_name || "Walk-in",
        amount: total,
        trans_date: new Date().toISOString().split("T")[0],
        note: `POS Sale - ${cart.length} items`,
        status: "PAID",
        details: cart.map((item) => ({
          product_id: item.id,
          qty: item.qty,
          unit_price: item.price,
          total: item.price * item.qty,
        })),
        discounts: [], // No discounts for now
        payments: [
          {
            method: "Cash",
            amount: total,
            trans_date: new Date().toISOString(),
          },
        ],
      };

      console.log("Creating transaction:", transactionData);

      // Create the transaction
      const response = await transactionsAPI.create(transactionData);
      const createdTransaction = apiUtils.formatResponse(response);

      console.log("Transaction created:", createdTransaction);

      // Clear the cart and show success
      clearCart();
      setError("");

      // Show success message with transaction number
      showNotification(
        `Transaction completed successfully! Transaction #: ${createdTransaction.txn_no}`,
        "success",
        true,
        {
          transaction: {
            ...createdTransaction,
            cashier_name:
              JSON.parse(localStorage.getItem("user") || "{}").username ||
              "N/A",
            customer: customer,
            date_created: new Date().toISOString(),
            service_charge_inclusive: serviceChargeInclusive,
          },
          cart: [...cart],
          customer: customer,
          subtotal,
          tax,
          total,
          totalDiscount,
          discounts: [...discounts],
          transactionNote,
          serviceCharge: serviceChargeAmount,
          serviceChargePercentage,
          serviceChargeInclusive,
          payments: [
            {
              payment_type: "Cash",
              payment_amount: total,
            },
          ],
          change: 0,
        }
      );
    } catch (error) {
      console.error("Error processing transaction:", error);
      setError(apiUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  };

  // Saved Transactions Functions
  const loadSavedTransactions = async () => {
    try {
      const response = await heldTransactionsAPI.list();
      const heldTransactions = apiUtils.formatResponse(response);
      setSavedTransactions(heldTransactions);
    } catch (error) {
      console.error('Error loading held transactions:', error);
      setError('Failed to load held transactions');
    }
  };

  const saveTransactionToLocal = async () => {
    if (cart.length === 0) {
      setError("Cart is empty");
      return;
    }

    try {
      const heldTransactionData = {
        cart: [...cart],
        customer,
        customerInput,
        discounts: [...discounts],
        transactionNote,
        subtotal,
        tax,
        total,
        totalDiscount,
        serviceCharge: serviceChargeAmount,
        serviceChargePercentage,
        serviceChargeInclusive,
      };

      const response = await heldTransactionsAPI.create(heldTransactionData);
      const savedTransaction = apiUtils.formatResponse(response);

      // Refresh the saved transactions list
      await loadSavedTransactions();

      // Clear current cart
      clearCart();
      setDiscounts([]);
      setTransactionNote("");

      showNotification(`Transaction saved to hold! Reference: ${savedTransaction.reference_number}`, "info");
    } catch (error) {
      console.error('Error saving transaction to hold:', error);
      setError('Failed to save transaction to hold');
    }
  };

  const loadSavedTransaction = async (savedTransaction) => {
    try {
      // Handle both old format (direct properties) and new format (transaction_data)
      const transactionData = savedTransaction.transaction_data || savedTransaction;
      
      setCart(transactionData.cart || []);
      setCustomer(transactionData.customer || null);
      setCustomerInput(transactionData.customerInput || "");
      setDiscounts(transactionData.discounts || []);
      setTransactionNote(transactionData.transactionNote || "");
      
      // Restore service charge settings if they exist in the saved transaction
      if (transactionData.hasOwnProperty('serviceCharge')) {
        setServiceChargeAmount(transactionData.serviceCharge || 0);
      }
      if (transactionData.hasOwnProperty('serviceChargePercentage')) {
        setServiceChargePercentage(transactionData.serviceChargePercentage || 0);
      }
      if (transactionData.hasOwnProperty('serviceChargeInclusive')) {
        setServiceChargeInclusive(transactionData.serviceChargeInclusive || false);
      }

      // Remove from saved transactions using reference number
      await heldTransactionsAPI.deleteByReference(savedTransaction.reference_number);
      
      // Refresh the saved transactions list
      await loadSavedTransactions();

      setSavedTransactionsDialogOpen(false);
      showNotification(`Transaction loaded! Reference: ${savedTransaction.reference_number}`, "success");
    } catch (error) {
      console.error('Error loading saved transaction:', error);
      setError('Failed to load saved transaction');
    }
  };

  const deleteSavedTransaction = async (transactionReferenceOrId) => {
    try {
      // Try to delete by reference number first, fall back to ID for backward compatibility
      try {
        await heldTransactionsAPI.deleteByReference(transactionReferenceOrId);
      } catch (refError) {
        // If reference deletion fails, try by ID
        await heldTransactionsAPI.delete(transactionReferenceOrId);
      }
      
      // Refresh the saved transactions list
      await loadSavedTransactions();
      
      showNotification("Held transaction deleted successfully", "success");
    } catch (error) {
      console.error('Error deleting saved transaction:', error);
      setError('Failed to delete saved transaction');
    }
  };

  // Payment Functions
  const handlePaymentDialogOpen = () => {
    setPayments([]);
    setCurrentPayment({
      payment_type: "CASH",
      reference1: "",
      reference2: "",
      payment_amount: 0,
    });
    setPaymentDialogOpen(true);
  };

  const handlePaymentDialogClose = () => {
    setPaymentDialogOpen(false);
  };

  const addPayment = () => {
    if (currentPayment.payment_amount <= 0) {
      return;
    }

    const newPayment = {
      ...currentPayment,
      id: Date.now(),
    };

    setPayments([...payments, newPayment]);
    setCurrentPayment({
      payment_type: "CASH",
      reference1: "",
      reference2: "",
      payment_amount: 0,
    });
  };

  const removePayment = (paymentId) => {
    setPayments(payments.filter((p) => p.id !== paymentId));
  };

  const getTotalPayments = () => {
    return payments.reduce((sum, payment) => sum + payment.payment_amount, 0);
  };

  const getChange = () => {
    const totalPaid = getTotalPayments();
    return Math.max(0, totalPaid - total);
  };

  const processPayment = async () => {
    const totalPaid = getTotalPayments();

    if (totalPaid < total) {
      setError("Insufficient payment amount");
      return;
    }

    try {
      setLoading(true);

      // First, create customer if it's a new one
      let customerId = customer?.id || 0;
      if (customer?.isNew && customer.display_name) {
        try {
          const newCustomerData = {
            display_name: customer.display_name,
            first_name: customer.display_name.split(" ")[0] || "",
            last_name:
              customer.display_name.split(" ").slice(1).join(" ") || "",
            mobile: "",
            address: "",
            notes: "Added from POS",
          };

          const customerResponse = await customersAPI.create(newCustomerData);
          const createdCustomer = apiUtils.formatResponse(customerResponse);
          customerId = createdCustomer.id;

          // Update customers list
          setCustomers([...customers, createdCustomer]);
        } catch (customerError) {
          console.warn(
            "Failed to create customer, using walk-in:",
            customerError
          );
          customerId = 0;
        }
      }

      // Prepare transaction data in the correct API format
      const grossTotal = cart.reduce(
        (sum, item) => sum + item.price * item.qty,
        0
      );
      const totalPaidAmount = getTotalPayments();
      const changeAmount = Math.max(0, totalPaidAmount - total);

      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

      const transactionData = {
        customer_id: customerId.toString(),
        user_id: currentUser.id || 0, // Use current user's ID, fallback to 0
        txn_no: "", // Let the API generate this
        note: transactionNote || "",
        subtotal: subtotal, // Net amount (VAT-exclusive)
        total_discounts: totalDiscount,
        grand_total: total, // VAT-inclusive total + service charge
        payment_total: totalPaidAmount,
        change: changeAmount,
        service_charge_amount: serviceChargeAmount, // Include service charge amount
        details: cart.map((item) => ({
          product_id: item.id.toString(),
          note: item.isComplimentary ? "COMPLIMENTARY" : "",
          qty: item.qty,
          unit_price: item.isComplimentary ? 0 : item.price,
          line_price: item.isComplimentary ? 0 : (item.price * item.qty),
          is_bundle: 0, // Assuming no bundles for now
        })),
        discounts: discounts.map((discount) => ({
          discount_type: discount.type,
          reference1: discount.reference1 || "",
          reference2: discount.reference2 || "",
          discount_percent:
            discount.discount_type === "percent"
              ? discount.discount_percent
              : 0,
          discount_amount:
            discount.discount_type === "amount"
              ? discount.discount_amount
              : (grossTotal * discount.discount_percent) / 100,
        })),
        payments: payments.map((payment) => ({
          payment_type: payment.payment_type,
          reference1: payment.reference1 || "",
          reference2: payment.reference2 || "",
          payment_amount: payment.payment_amount,
        })),
        date_created: new Date()
          .toISOString()
          .replace("T", " ")
          .substring(0, 19),
      };

      console.log("Creating transaction:", transactionData);

      // Create the transaction
      const response = await transactionsAPI.create(transactionData);
      const createdTransaction = apiUtils.formatResponse(response);

      console.log("Transaction created:", createdTransaction);

      // Refresh product stock to show updated quantities
      await refreshProductStock();

      // Clear the cart and show success
      clearCart();
      setDiscounts([]);
      setTransactionNote("");
      setError("");
      handlePaymentDialogClose();
      
      // Close mobile cart sidebar if on mobile
      setMobileCartOpen(false);

      // Show success message with transaction number and change
      const change = getChange();
      const changeMessage = change > 0 ? ` Change: ₱${change.toFixed(2)}` : "";
      showNotification(
        `Transaction completed successfully! Transaction #: ${createdTransaction.txn_no}${changeMessage}`,
        "success",
        true,
        {
          transaction: {
            ...createdTransaction,
            cashier_name:
              JSON.parse(localStorage.getItem("user") || "{}").username ||
              "N/A",
            customer: customer,
            date_created: new Date().toISOString(),
          },
          cart: [...cart],
          customer: customer,
          subtotal,
          tax,
          total,
          totalDiscount,
          serviceChargeAmount,
          serviceChargeEnabled,
          serviceChargePercentage,
          discounts: [...discounts],
          transactionNote,
          payments: [...payments],
          change: changeAmount,
        }
      );
    } catch (error) {
      console.error("Error processing transaction:", error);
      setError(apiUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Mobile Layout
  const mobileLayout = (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
        {/* Mobile Top Bar */}
        <AppBar 
          position="static" 
          elevation={1}
          sx={{ 
            bgcolor: "background.paper", 
            color: "text.primary",
            borderBottom: 1,
            borderColor: "divider"
          }}
        >
          <Toolbar sx={{ py: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1, color: "text.primary" }}>
              POS
            </Typography>
            
            {/* Combined Cart Icon and Total Container */}
            <Box
              onClick={() => cart.length > 0 && setMobileCartOpen(true)}
              sx={{ 
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                cursor: cart.length > 0 ? "pointer" : "default",
                bgcolor: cart.length > 0 ? "primary.main" : "transparent",
                color: cart.length > 0 ? "primary.contrastText" : "text.primary",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "scale(1)",
                "&:hover": cart.length > 0 ? {
                  bgcolor: "primary.dark",
                  transform: "scale(1.02)",
                  boxShadow: 3,
                } : {
                  bgcolor: "action.hover",
                },
                "&:active": cart.length > 0 ? {
                  transform: "scale(0.98)",
                  transition: "transform 0.1s ease-in-out",
                } : {},
                // Add ripple effect only when cart has items
                position: "relative",
                overflow: "hidden",
                "&::before": cart.length > 0 ? {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: 0,
                  height: 0,
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.3)",
                  transform: "translate(-50%, -50%)",
                  transition: "width 0.6s, height 0.6s",
                } : {},
                "&:active::before": cart.length > 0 ? {
                  width: "300px",
                  height: "300px",
                } : {},
                minWidth: cart.length > 0 ? 120 : 48,
                minHeight: 48,
              }}
            >
              {/* Cart Icon with Badge */}
              <Box sx={{ position: "relative" }}>
                <Badge 
                  badgeContent={cart.length} 
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      right: -6,
                      top: -6,
                      fontSize: "0.75rem",
                      minWidth: 18,
                      height: 18,
                      transform: cart.length > 0 ? "scale(1)" : "scale(0)",
                      transition: "transform 0.2s ease-in-out",
                    }
                  }}
                >
                  <ShoppingCart 
                    sx={{ 
                      fontSize: 24,
                      transition: "transform 0.2s ease-in-out",
                      transform: cart.length > 0 ? "scale(1.1)" : "scale(1)",
                    }} 
                  />
                </Badge>
              </Box>
              
              {/* Total Display */}
              {cart.length > 0 && (
                <Box 
                  sx={{ 
                    textAlign: "right",
                    opacity: 1,
                    transform: "translateX(0)",
                    transition: "all 0.3s ease-in-out",
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: "block",
                      lineHeight: 1,
                      opacity: 0.9,
                      fontSize: "0.7rem",
                    }}
                  >
                    Total
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: "bold",
                      lineHeight: 1,
                      fontSize: "1rem",
                    }}
                  >
                    ₱{total.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        {/* Search Bar */}
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: "text.secondary" }} />
              ),
              endAdornment: searchTerm && (
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm("")}
                  sx={{ mr: 1 }}
                >
                  <Clear />
                </IconButton>
              ),
            }}
          />
        </Box>

        {/* Category Filter */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Autocomplete
            value={selectedCategory}
            onChange={(event, newValue) => {
              setSelectedCategory(newValue || "All Categories");
            }}
            options={categories}
            size="small"
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Category"
                size="small"
              />
            )}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error}
          </Alert>
        )}

        {/* Products Grid - Scrollable */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2,
            pb: 2,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50%",
              }}
            >
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading products...</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredProducts.map((product) => (
                <Grid
                  item
                  xs={6}
                  sm={4}
                  key={product.id}
                >
                  <Card
                    sx={{
                      cursor: "pointer",
                      height: 180,
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      overflow: "hidden",
                      backgroundImage: product?.img_url
                        ? `url("${process.env.REACT_APP_IMG_HOST}/api/v1/images/${product.img_url}")`
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      backgroundColor: "#f5f5f5",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 4,
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }}
                    onClick={() => addToCart(product)}
                  >
                    {/* Stock chip at top right */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 2,
                      }}
                    >
                      <Chip
                        label={parseInt(product?.on_hand || 0, 10)}
                        size="small"
                        sx={{
                          color: "white",
                          backgroundColor:
                            (product?.on_hand || 0) > 10
                              ? "success.main"
                              : (product?.on_hand || 0) > 0
                              ? "warning.main"
                              : "error.main",
                          fontWeight: "bold",
                          minWidth: "40px",
                        }}
                      />
                    </Box>

                    {/* Spacer to push content to bottom */}
                    <Box sx={{ flex: 1 }} />

                    {/* Product details */}
                    <Box
                      sx={{
                        backgroundColor: "rgba(0, 0, 0, 0.75)",
                        color: "white",
                        p: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        mt: "auto",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        component="div"
                        sx={{
                          fontWeight: "bold",
                          mb: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.85rem",
                          color: "white",
                        }}
                      >
                        {product?.name || "Unknown Product"}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.8,
                            fontSize: "0.7rem",
                            color: "rgba(255, 255, 255, 0.8)",
                          }}
                        >
                          {product?.sku || "No SKU"}
                        </Typography>

                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.9rem",
                            color: "#4CAF50",
                          }}
                        >
                          ₱{parseFloat(product?.price || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}

              {filteredProducts.length === 0 && !loading && (
                <Box sx={{ textAlign: "center", py: 4, width: "100%" }}>
                  <Typography
                    color="text.secondary"
                    variant="h6"
                    gutterBottom
                  >
                    {searchTerm
                      ? "No products found matching your search."
                      : "No products available."}
                  </Typography>
                  {!searchTerm && (
                    <Typography color="text.secondary" variant="body2">
                      Add some products to get started with POS.
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          )}
        </Box>

        {/* Mobile Cart Drawer */}
        <Drawer
          anchor="right"
          open={mobileCartOpen}
          onClose={() => setMobileCartOpen(false)}
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: 400,
              height: "100dvh", // Use dynamic viewport height for mobile
              display: "flex",
              flexDirection: "column",
              overflow: "hidden", // Prevent drawer itself from scrolling
            }
          }}
        >
          {/* Cart Header - Fixed at Top */}
          <Box
            sx={{
              flexShrink: 0,
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ShoppingCart />
                Cart ({cart.length})
              </Typography>
            </Box>

            {/* Action Buttons - First Row */}
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              {cart.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<ClearAll />}
                  onClick={clearCart}
                  sx={{ flex: 1 }}
                >
                  Clear
                </Button>
              )}

              {cart.length > 0 && orderSlipEnabled && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<RestaurantMenu />}
                  onClick={handleOrderSlipClick}
                  sx={{ flex: 1 }}
                >
                  Order Slip
                </Button>
              )}

              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<CloseIcon />}
                onClick={() => setMobileCartOpen(false)}
                sx={{ flex: 1 }}
              >
                Close
              </Button>
            </Box>

            {/* Action Buttons - Second Row */}
            <Box sx={{ display: "flex", gap: 1 }}>
              {cart.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<Pause />}
                  onClick={saveTransactionToLocal}
                  sx={{ flex: 1 }}
                >
                  Hold
                </Button>
              )}

              {savedTransactions.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  startIcon={<ListIcon />}
                  onClick={() => setSavedTransactionsDialogOpen(true)}
                  sx={{ flex: 1 }}
                >
                  Saved ({savedTransactions.length})
                </Button>
              )}
            </Box>
          </Box>

          {/* Customer Selection - Fixed */}
          <Box
            sx={{
              flexShrink: 0,
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Autocomplete
              value={customer}
              onChange={handleCustomerChange}
              inputValue={customerInput}
              onInputChange={(event, newInputValue) =>
                setCustomerInput(newInputValue)
              }
              options={customers}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                if (option.inputValue) {
                  return option.inputValue;
                }
                return option.display_name || "";
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.display_name
                    ?.toLowerCase()
                    .includes(params.inputValue.toLowerCase())
                );

                const { inputValue } = params;
                const isExisting = options.some(
                  (option) => inputValue === option.display_name
                );
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    inputValue,
                    display_name: `Add "${inputValue}" as new customer`,
                  });
                }

                return filtered;
              }}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  placeholder="Search or add customer..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <Person sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Scrollable Cart Items - Fills remaining space */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
              WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
            }}
          >
            {cart.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary">
                  Cart is empty. Tap products to add them.
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {cart.map((item) => (
                  <React.Fragment key={item.cartItemId}>
                    <ListItem sx={{ px: 2, py: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {item.name}
                            </Typography>
                            {item.isComplimentary && (
                              <Chip 
                                label="COMPLIMENTARY" 
                                size="small" 
                                color="success"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.sku} • {item.isComplimentary ? '0.00' : `₱${item.price.toFixed(2)}`} each
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box
                          sx={{ 
                            display: "flex", 
                            flexDirection: "column",
                            alignItems: "flex-end", 
                            gap: 0.5 
                          }}
                        >
                          {/* Top row: Quantity controls */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                updateCartQuantity(item.cartItemId, item.qty - 1)
                              }
                            >
                              <Remove fontSize="small" />
                            </IconButton>

                            <Typography
                              variant="body2"
                              sx={{ minWidth: 30, textAlign: "center" }}
                            >
                              {item.qty}
                            </Typography>

                            <IconButton
                              size="small"
                              onClick={() =>
                                updateCartQuantity(item.cartItemId, item.qty + 1)
                              }
                              disabled={
                                cart
                                  .filter(ci => ci.id === item.id)
                                  .reduce((sum, ci) => sum + ci.qty, 0) >= item.on_hand
                              }
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          {/* Bottom row: Action buttons */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color={item.isComplimentary ? "success" : "default"}
                              onClick={() => toggleComplimentary(item.id, item.isComplimentary)}
                              title={item.isComplimentary ? "Mark as Regular" : "Mark as Complimentary"}
                            >
                              <CardGiftcard fontSize="small" />
                            </IconButton>

                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeFromCart(item.cartItemId)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>

                    <Box sx={{ px: 2, pb: 1 }}>
                      <Typography
                        variant="body2"
                        align="right"
                        fontWeight="bold"
                      >
                        ₱{item.isComplimentary ? '0.00' : (item.price * item.qty).toFixed(2)}
                      </Typography>
                    </Box>

                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Bottom Section - Fixed at Bottom (only when cart has items) */}
          {cart.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                backgroundColor: "background.paper",
                borderTop: 1,
                borderColor: "divider",
                paddingBottom: "env(safe-area-inset-bottom, 0px)", // Safe area for iOS
              }}
            >
              {/* Note and Discount Buttons */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Note />}
                    onClick={handleNoteDialogOpen}
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    Note
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Discount />}
                    onClick={handleDiscountDialogOpen}
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    Discount
                  </Button>
                </Box>

                {/* Show current note */}
                {transactionNote && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      mb: 0.5,
                    }}
                  >
                    Note: {transactionNote}
                  </Typography>
                )}

                {/* Show total discounts */}
                {discounts.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Discounts ({discounts.length}) applied
                  </Typography>
                )}
              </Box>

              {/* Cart Summary */}
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">Subtotal (Net):</Typography>
                  <Typography variant="body2">
                    ₱{subtotal.toFixed(2)}
                  </Typography>
                </Box>
                {totalDiscount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="error">
                      Discount:
                    </Typography>
                    <Typography variant="body2" color="error">
                      -₱{totalDiscount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {serviceChargeEnabled && serviceChargeAmount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="secondary">
                      Service Charge ({serviceChargePercentage}% {serviceChargeInclusive ? 'Inc.' : 'Exc.'}):
                    </Typography>
                    <Typography variant="body2" color="secondary">
                      ₱{serviceChargeAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">VAT (12%):</Typography>
                  <Typography variant="body2">₱{tax.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Total (VAT Inc.):
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ₱{total.toFixed(2)}
                  </Typography>
                </Box>
                
                {/* Checkout Button */}
                <Button
                  variant="contained"
                  onClick={handlePaymentDialogOpen}
                  startIcon={<Payment />}
                  size="large"
                  fullWidth
                  sx={{ py: 2 }}
                >
                  Checkout
                </Button>
              </Box>
            </Box>
          )}
        </Drawer>
      </Box>
    );

  // Desktop Layout
  const desktopLayout = (
    <Box
      sx={{
        height: "calc(100vh - 64px)", // Account for AppBar height (64px is standard)
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        p: { xs: 1, sm: 2 }, // Put padding back
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* Products Grid - 70% */}
        <Box
          sx={{
            flex: 7,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            pr: 1,
          }}
        >
          {/* Search Bar and Category Filter - Fixed at top */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              flexShrink: 0,
            }}
          >
            <TextField
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ width: "70%" }}
              InputProps={{
                startAdornment: (
                  <Search sx={{ mr: 1, color: "text.secondary" }} />
                ),
                endAdornment: searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{ mr: 1 }}
                  >
                    <Clear />
                  </IconButton>
                ),
              }}
            />
            <Autocomplete
              value={selectedCategory}
              onChange={(event, newValue) => {
                setSelectedCategory(newValue || "All Categories");
              }}
              options={categories}
              size="small"
              sx={{ width: "30%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Category"
                  size="small"
                />
              )}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
              {error}
            </Alert>
          )}

          {/* Scrollable Grid Container */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              pb: 2,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50%",
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading products...</Typography>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ width: "100%", m: 0, pb: 2 }}>
                {filteredProducts.map((product) => {
                  return (
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      md={3}
                      lg={3}
                      xl={2.4}
                      key={product.id}
                    >
                      <Card
                        sx={{
                          cursor: "pointer",
                          height: 200,
                          width: "100%",
                          minWidth: 160,
                          maxWidth: 200,
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          overflow: "hidden",
                          backgroundImage: product?.img_url
                            ? `url("${process.env.REACT_APP_IMG_HOST}/api/v1/images/${product.img_url}")`
                            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          backgroundColor: "#f5f5f5",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 6,
                          },
                        }}
                        onClick={() => addToCart(product)}
                      >
                        {/* Stock chip at top right */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                          }}
                        >
                          <Chip
                            label={parseInt(product?.on_hand || 0, 10)}
                            size="small"
                            sx={{
                              color: "white",
                              backgroundColor:
                                (product?.on_hand || 0) > 10
                                  ? "success.main"
                                  : (product?.on_hand || 0) > 0
                                  ? "warning.main"
                                  : "error.main",
                              fontWeight: "bold",
                              minWidth: "40px",
                            }}
                          />
                        </Box>

                        {/* Spacer to push content to bottom */}
                        <Box sx={{ flex: 1 }} />

                        {/* Semi-transparent black container for product details */}
                        <Box
                          sx={{
                            backgroundColor: "rgba(0, 0, 0, 0.75)",
                            color: "white",
                            p: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            mt: "auto",
                          }}
                        >
                          {/* Product name */}
                          <Typography
                            variant="subtitle2"
                            component="div"
                            sx={{
                              fontWeight: "bold",
                              mb: 0.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.85rem",
                              color: "white",
                            }}
                          >
                            {product?.name || "Unknown Product"}
                          </Typography>

                          {/* SKU and Price row */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: 0.8,
                                fontSize: "0.7rem",
                                color: "rgba(255, 255, 255, 0.8)",
                              }}
                            >
                              {product?.sku || "No SKU"}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.9rem",
                                color: "#4CAF50",
                              }}
                            >
                              ₱{parseFloat(product?.price || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}

                {filteredProducts.length === 0 && !loading && (
                  <Box sx={{ textAlign: "center", py: 4, width: "100%" }}>
                    <Typography
                      color="text.secondary"
                      variant="h6"
                      gutterBottom
                    >
                      {searchTerm
                        ? "No products found matching your search."
                        : "No products available."}
                    </Typography>
                    {!searchTerm && (
                      <Typography color="text.secondary" variant="body2">
                        Add some products to get started with POS.
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
            )}
          </Box>
        </Box>
        {/* Cart - 30% */}
        <Paper
          sx={{
            flex: 3,
            display: "flex",
            flexDirection: "column",
            minWidth: 300,
            maxWidth: 400,
            height: "100%",
            overflow: "hidden",
            position: "relative", // Enable absolute positioning for children
          }}
        >
          {/* Cart Header - Fixed */}
          <Box
            sx={{
              flexShrink: 0,
              p: 1.5,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <ShoppingCart />
                Cart ({cart.length})
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {/* Clear Cart Button */}
                {cart.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={clearCart}
                    title="Clear Cart"
                    color="error"
                  >
                    <ClearAll />
                  </IconButton>
                )}

                {/* Hold Transaction Button */}
                {cart.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={saveTransactionToLocal}
                    title="Hold Transaction"
                    color="warning"
                  >
                    <Pause />
                  </IconButton>
                )}

                {/* Order Slip Button */}
                {cart.length > 0 && orderSlipEnabled && (
                  <IconButton
                    size="small"
                    onClick={handleOrderSlipClick}
                    title="Print Order Slip"
                    color="secondary"
                  >
                    <RestaurantMenu />
                  </IconButton>
                )}

                {/* Saved Transactions Button */}
                {savedTransactions.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={() => setSavedTransactionsDialogOpen(true)}
                    title={`View ${savedTransactions.length} saved transaction(s)`}
                    color="info"
                  >
                    <ListIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>

          {/* Customer Selection - Fixed */}
          <Box
            sx={{
              flexShrink: 0,
              p: 1.5,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Autocomplete
              value={customer}
              onChange={handleCustomerChange}
              inputValue={customerInput}
              onInputChange={(event, newInputValue) =>
                setCustomerInput(newInputValue)
              }
              options={customers}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                if (option.inputValue) {
                  return option.inputValue;
                }
                return option.display_name || "";
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.display_name
                    ?.toLowerCase()
                    .includes(params.inputValue.toLowerCase())
                );

                const { inputValue } = params;
                const isExisting = options.some(
                  (option) => inputValue === option.display_name
                );
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    inputValue,
                    display_name: `Add "${inputValue}" as new customer`,
                  });
                }

                return filtered;
              }}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  placeholder="Search or add customer..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <Person sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Cart Items - Scrollable */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
              maxHeight: "230px", // Further reduced to ensure buttons are fully visible
            }}
          >
            {cart.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary">
                  Cart is empty. Tap products to add them.
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {cart.map((item) => (
                  <React.Fragment key={item.cartItemId}>
                    <ListItem sx={{ px: 2, py: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {item.name}
                            </Typography>
                            {item.isComplimentary && (
                              <Chip 
                                label="COMPLIMENTARY" 
                                size="small" 
                                color="success"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.sku} • {item.isComplimentary ? '0.00' : `₱${item.price.toFixed(2)}`} each
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box
                          sx={{ 
                            display: "flex", 
                            flexDirection: "column",
                            alignItems: "flex-end", 
                            gap: 0.5 
                          }}
                        >
                          {/* Top row: Quantity controls */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                updateCartQuantity(item.cartItemId, item.qty - 1)
                              }
                            >
                              <Remove fontSize="small" />
                            </IconButton>

                            <Typography
                              variant="body2"
                              sx={{ minWidth: 30, textAlign: "center" }}
                            >
                              {item.qty}
                            </Typography>

                            <IconButton
                              size="small"
                              onClick={() =>
                                updateCartQuantity(item.cartItemId, item.qty + 1)
                              }
                              disabled={
                                cart
                                  .filter(ci => ci.id === item.id)
                                  .reduce((sum, ci) => sum + ci.qty, 0) >= item.on_hand
                              }
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          {/* Bottom row: Action buttons */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color={item.isComplimentary ? "success" : "default"}
                              onClick={() => toggleComplimentary(item.id, item.isComplimentary)}
                              title={item.isComplimentary ? "Mark as Regular" : "Mark as Complimentary"}
                            >
                              <CardGiftcard fontSize="small" />
                            </IconButton>

                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeFromCart(item.cartItemId)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>

                    <Box sx={{ px: 2, pb: 1 }}>
                      <Typography
                        variant="body2"
                        align="right"
                        fontWeight="bold"
                      >
                        ₱{item.isComplimentary ? '0.00' : (item.price * item.qty).toFixed(2)}
                      </Typography>
                    </Box>

                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Note and Discount Actions */}
          {cart.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                p: 2,
                borderTop: 1,
                borderColor: "divider",
                maxHeight: "120px",
                overflow: "auto",
                pt: "2px",
                mb: "210px", // Increased from 200px to 250px (added 50px more)
              }}
            >
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Note />}
                  onClick={handleNoteDialogOpen}
                  size="small"
                  sx={{ flex: 1 }}
                >
                  Note
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Discount />}
                  onClick={handleDiscountDialogOpen}
                  size="small"
                  sx={{ flex: 1 }}
                >
                  Discount
                </Button>
              </Box>

              {/* Show current note */}
              {transactionNote && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Note:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {transactionNote}
                  </Typography>
                </Box>
              )}

              {/* Show total discounts */}
              {discounts.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Discounts ({discounts.length}):
                  </Typography>
                  {discounts.map((discount) => (
                    <Box
                      key={discount.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: "32px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          mr: 1,
                        }}
                      >
                        {discount.type} -{" "}
                        {discount.discount_type === "amount"
                          ? `₱${discount.discount_amount.toFixed(2)}`
                          : `${discount.discount_percent}%`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeDiscount(discount.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Cart Summary - Anchored to Bottom */}
          {cart.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                p: 1.5,
                borderTop: 1,
                borderColor: "divider",
                backgroundColor: "background.paper",
                mb: "30px",
                zIndex: 10, // Ensure it stays on top
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">Subtotal (Net):</Typography>
                  <Typography variant="body2">
                    ₱{subtotal.toFixed(2)}
                  </Typography>
                </Box>
                {totalDiscount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="error">
                      Discount:
                    </Typography>
                    <Typography variant="body2" color="error">
                      -₱{totalDiscount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {serviceChargeEnabled && serviceChargeAmount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="secondary">
                      Service Charge ({serviceChargePercentage}% {serviceChargeInclusive ? 'Inc.' : 'Exc.'}):
                    </Typography>
                    <Typography variant="body2" color="secondary">
                      ₱{serviceChargeAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">VAT (12%):</Typography>
                  <Typography variant="body2">₱{tax.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Total (VAT Inc.):
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ₱{total.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={handlePaymentDialogOpen}
                startIcon={<Payment />}
                size="large"
                fullWidth
                sx={{ py: 2 }}
              >
                Checkout
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
    );

  // Render the appropriate layout and always include dialogs
  return (
    <>
      {isMobile ? mobileLayout : desktopLayout}

      {/* Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={handleNoteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Transaction Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={tempNote}
            onChange={(e) => setTempNote(e.target.value)}
            placeholder="Enter transaction note..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNoteDialogClose}>Cancel</Button>
          <Button onClick={handleNoteSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog
        open={discountDialogOpen}
        onClose={handleDiscountDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Discount</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Discount Type */}
            <FormControl fullWidth>
              <InputLabel>Discount Type</InputLabel>
              <Select
                value={tempDiscount.type}
                label="Discount Type"
                onChange={(e) =>
                  setTempDiscount({ ...tempDiscount, type: e.target.value })
                }
              >
                <MenuItem value="Store">Store</MenuItem>
                <MenuItem value="Senior Citizen">Senior Citizen</MenuItem>
                <MenuItem value="PWD">PWD</MenuItem>
              </Select>
            </FormControl>

            {/* Amount or Percent */}
            <FormControl fullWidth>
              <InputLabel>Discount Method</InputLabel>
              <Select
                value={tempDiscount.discount_type}
                label="Discount Method"
                onChange={(e) =>
                  setTempDiscount({
                    ...tempDiscount,
                    discount_type: e.target.value,
                  })
                }
              >
                <MenuItem value="amount">Fixed Amount</MenuItem>
                <MenuItem value="percent">Percentage</MenuItem>
              </Select>
            </FormControl>

            {/* Discount Value */}
            {tempDiscount.discount_type === "amount" ? (
              <TextField
                label="Discount Amount"
                type="number"
                fullWidth
                value={tempDiscount.discount_amount}
                onChange={(e) =>
                  setTempDiscount({
                    ...tempDiscount,
                    discount_amount: parseFloat(e.target.value) || 0,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₱</InputAdornment>
                  ),
                }}
              />
            ) : (
              <TextField
                label="Discount Percentage"
                type="number"
                fullWidth
                value={tempDiscount.discount_percent}
                onChange={(e) =>
                  setTempDiscount({
                    ...tempDiscount,
                    discount_percent: parseFloat(e.target.value) || 0,
                  })
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, max: 100 }}
              />
            )}

            {/* Reference Fields */}
            <TextField
              label="Reference 1"
              fullWidth
              value={tempDiscount.reference1}
              onChange={(e) =>
                setTempDiscount({ ...tempDiscount, reference1: e.target.value })
              }
              placeholder="e.g., ID Number, Voucher Code"
            />

            <TextField
              label="Reference 2"
              fullWidth
              value={tempDiscount.reference2}
              onChange={(e) =>
                setTempDiscount({ ...tempDiscount, reference2: e.target.value })
              }
              placeholder="e.g., Authorization Code"
            />

            {/* Current Discounts List */}
            {discounts.length > 0 && (
              <>
                <Divider sx={{ mt: 2, mb: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Current Discounts ({discounts.length})
                </Typography>
                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {discounts.map((discount) => (
                    <ListItem
                      key={discount.id}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: "grey.50"
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => removeDiscount(discount.id)}
                          size="small"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {discount.type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {discount.discount_type === "amount" 
                                ? `₱${discount.discount_amount.toFixed(2)}` 
                                : `${discount.discount_percent}%`}
                              {discount.reference1 && ` • ${discount.reference1}`}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscountDialogClose}>Cancel</Button>
          <Button
            onClick={handleDiscountSave}
            variant="contained"
            disabled={
              (tempDiscount.discount_type === "amount" &&
                tempDiscount.discount_amount <= 0) ||
              (tempDiscount.discount_type === "percent" &&
                tempDiscount.discount_percent <= 0)
            }
          >
            Add Discount
          </Button>
        </DialogActions>
      </Dialog>

      {/* Saved Transactions Dialog */}
      <Dialog
        open={savedTransactionsDialogOpen}
        onClose={() => setSavedTransactionsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Saved Transactions</DialogTitle>
        <DialogContent>
          {/* System Warning Note */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Loading a transaction will take it off this list. If it is not yet checked out, you must save it again when processing another transaction.
            </Typography>
          </Alert>

          {savedTransactions.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              No saved transactions found.
            </Typography>
          ) : (
            <List>
              {savedTransactions.map((transaction) => (
                <ListItem
                  key={transaction.reference_number || transaction.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    mb: 1,
                    borderRadius: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="subtitle2">
                          {transaction.reference_number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(transaction.timestamp || transaction.date_created).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(transaction.cart?.length || transaction.transaction_data?.cart?.length || 0)} items •{" "}
                          {(transaction.customer?.display_name || transaction.transaction_data?.customer?.display_name || "Walk-in")}
                        </Typography>
                        {transaction.created_by_user && (
                          <Typography variant="caption" color="text.secondary">
                            Created by: {transaction.created_by_user.name}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        ₱{(transaction.total || transaction.transaction_data?.total || 0).toFixed(2)}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => loadSavedTransaction(transaction)}
                      >
                        Load
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteSavedTransaction(transaction.reference_number || transaction.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSavedTransactionsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handlePaymentDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Transaction Summary */}
            <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
              <Typography variant="subtitle2" gutterBottom color="text.primary">
                Transaction Summary
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2" color="text.primary">
                  Subtotal (Net):
                </Typography>
                <Typography variant="body2" color="text.primary">
                  ₱{subtotal.toFixed(2)}
                </Typography>
              </Box>
              {totalDiscount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="error">
                    Discount:
                  </Typography>
                  <Typography variant="body2" color="error">
                    -₱{totalDiscount.toFixed(2)}
                  </Typography>
                </Box>
              )}
              {serviceChargeEnabled && serviceChargeAmount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="secondary">
                    Service Charge ({serviceChargePercentage}% {serviceChargeInclusive ? 'Inc.' : 'Exc.'}):
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    ₱{serviceChargeAmount.toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2" color="text.primary">
                  VAT (12%):
                </Typography>
                <Typography variant="body2" color="text.primary">
                  ₱{tax.toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.primary"
                >
                  Total (VAT Inc.):
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary"
                >
                  ₱{total.toFixed(2)}
                </Typography>
              </Box>
            </Paper>

            {/* Payment Form */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Payment Type</InputLabel>
                <Select
                  value={currentPayment.payment_type}
                  label="Payment Type"
                  onChange={(e) =>
                    setCurrentPayment({
                      ...currentPayment,
                      payment_type: e.target.value,
                    })
                  }
                >
                  <MenuItem value="CASH">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Money /> Cash
                    </Box>
                  </MenuItem>
                  <MenuItem value="GCASH">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccountBalance /> GCash
                    </Box>
                  </MenuItem>
                  <MenuItem value="CARD">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CreditCard /> Card
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Payment Amount"
                type="number"
                fullWidth
                value={currentPayment.payment_amount}
                onChange={(e) =>
                  setCurrentPayment({
                    ...currentPayment,
                    payment_amount: parseFloat(e.target.value) || 0,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₱</InputAdornment>
                  ),
                }}
              />

              {/* Cash Denomination Buttons */}
              {currentPayment.payment_type === "CASH" && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Quick Add:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {/* Exact Amount Button */}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const remainingAmount = Math.max(
                          0,
                          total - getTotalPayments()
                        );
                        setCurrentPayment({
                          ...currentPayment,
                          payment_amount: remainingAmount,
                        });
                      }}
                      sx={{ minWidth: 60 }}
                      disabled={total - getTotalPayments() <= 0}
                    >
                      Exact
                    </Button>
                    {[1, 20, 50, 100, 200, 500, 1000].map((denomination) => (
                      <Button
                        key={denomination}
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setCurrentPayment({
                            ...currentPayment,
                            payment_amount:
                              currentPayment.payment_amount + denomination,
                          })
                        }
                        sx={{ minWidth: 60 }}
                      >
                        ₱{denomination}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {currentPayment.payment_type !== "CASH" && (
                <>
                  <TextField
                    label="Reference 1"
                    fullWidth
                    value={currentPayment.reference1}
                    onChange={(e) =>
                      setCurrentPayment({
                        ...currentPayment,
                        reference1: e.target.value,
                      })
                    }
                    placeholder={
                      currentPayment.payment_type === "GCASH"
                        ? "Transaction ID"
                        : "Card Number (last 4 digits)"
                    }
                  />
                  <TextField
                    label="Reference 2"
                    fullWidth
                    value={currentPayment.reference2}
                    onChange={(e) =>
                      setCurrentPayment({
                        ...currentPayment,
                        reference2: e.target.value,
                      })
                    }
                    placeholder={
                      currentPayment.payment_type === "GCASH"
                        ? "Phone Number"
                        : "Authorization Code"
                    }
                  />
                </>
              )}

              <Button
                variant="outlined"
                onClick={addPayment}
                disabled={currentPayment.payment_amount <= 0}
                fullWidth
              >
                Add Payment
              </Button>
            </Box>

            {/* Payments List */}
            {payments.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Payments Added
                </Typography>
                <List dense>
                  {payments.map((payment) => (
                    <ListItem key={payment.id}>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2">
                              {payment.payment_type} - ₱
                              {payment.payment_amount.toFixed(2)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removePayment(payment.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                        secondary={
                          payment.reference1 && `Ref: ${payment.reference1}`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">Total Paid:</Typography>
                  <Typography variant="subtitle2">
                    ₱{getTotalPayments().toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">Amount Due:</Typography>
                  <Typography variant="subtitle2">
                    ₱{total.toFixed(2)}
                  </Typography>
                </Box>
                {getTotalPayments() >= total && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="subtitle2" color="success.main">
                      Change:
                    </Typography>
                    <Typography variant="subtitle2" color="success.main">
                      ₱{getChange().toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={processPayment}
            disabled={getTotalPayments() < total || payments.length === 0}
          >
            Complete Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Thermal Receipt Preview Dialog */}
      <Dialog
        open={receiptPreviewOpen}
        onClose={() => setReceiptPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "90vh" },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Receipt />
          Thermal Receipt Preview
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Preview of how the receipt will look on a {thermalWidth}mm thermal
              printer:
            </Typography>
            {receiptData && (
              <ThermalReceiptPreview
                transaction={receiptData.transaction}
                transactionDetails={{
                  details:
                    receiptData.cart?.map((item) => ({
                      product: { name: item.name },
                      qty: item.qty,
                      unit_price: item.price,
                    })) || [],
                  payments: receiptData.payments || [],
                }}
                thermalWidth={thermalWidth}
                showPrintButton={true}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Order Slip Preview Dialog */}
      <Dialog 
        open={orderSlipPreviewOpen} 
        onClose={() => setOrderSlipPreviewOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestaurantMenu />
          Order Slip Preview
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Preview of the order slip for kitchen/preparation:
            </Typography>
            {orderSlipData && (
              <OrderSlipPreview
                orderData={orderSlipData}
                thermalWidth={thermalWidth}
                showPrintButton={true}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderSlipPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.showPrintButton ? 8000 : 4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
          action={
            notification.showPrintButton && (
              <Button
                color="inherit"
                size="small"
                startIcon={<Print />}
                onClick={handlePrintReceiptClick}
                autoFocus
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                  },
                  mr: 1,
                }}
              >
                Print Receipt
              </Button>
            )
          }
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default POS;
